import classNames from 'classnames';
import { CSSProperties, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { useCheckoutState } from '../../../utils/checkoutState';
import { config, getAssetUrl } from '../../../utils/config';
import { getStepFromRoute } from '../../../utils/routing';
import useIsMobileDevice from '../../../utils/useIsMobileDevice';
import Collapse from '../../atoms/Collapse';
import MainHeadline from '../../atoms/MainHeadline';
import Paragraph from '../../atoms/Paragraph';
import Price from '../../atoms/Price';
import SubHeadline from '../../atoms/SubHeadline';
import BasketItem from '../../molecules/BasketItem';
import BasketSummaryBadges from '../../molecules/BasketSummaryBadges';
import { getCarrierI18nKey } from '../../molecules/CarrierOptions';
import DiscountCodes from '../../molecules/DiscountCodes';
import PriceSummary from '../../molecules/PriceSummary';
import groupItems from './groupItems';
import './style.scss';

const Basket = ({ desktopStickyStyle }: { desktopStickyStyle?: CSSProperties }) => {
  const {
    checkoutState: {
      orderState: { basket, shipping },
      integrations: { promotion }
    },
    scrollRefs: { basketRef },
    filledSlots
  } = useCheckoutState();

  const basketTotal = basket.total.costCapture ?? basket.total.costWithTaxWithVouchersWithServiceCostsWithGiftCard;
  // const basketSubtotal = basket.total.costWithTaxWithoutVouchersWithoutServiceCosts;
  const basketShippingCost = basket.total.costShippingServiceCosts;
  const selectedCarrier = shipping?.selected?.[0].carrierGroupKey;

  const isMobile = useIsMobileDevice();
  const location = useLocation();

  const filteredPackages =
    config.basket.showSoldOutItems ? basket.packages : basket.packages.filter(({ isAvailable }) => isAvailable);

  const isBasketTop = config.basket.position === 'top';
  const isAuthStep = getStepFromRoute(location.pathname) === 'auth';
  const isPaymentStep = getStepFromRoute(location.pathname) === 'payment';
  const isCollapsible = isMobile && (isAuthStep || (isPaymentStep && isBasketTop));
  const [isExpanded, setIsExpanded] = useState(!isMobile || !isBasketTop);
  const [showDeliveryBadge, setShowDeliveryBadge] = useState(isMobile && !isExpanded);
  const isBreadcrumbsEnabled = config.features.breadcrumbs.enabled;
  const arePromotionsEnabled = promotion?.codes?.enabled;
  const discountCodesEnabled = arePromotionsEnabled || config.features.vouchers.enabled;
  const showDiscountInBasket = config.features.discountCodes.position === 'basket';
  const isBasketBottom = config.basket.position === 'bottom';
  const showDiscountCodes = (!isMobile || isBasketBottom || isAuthStep) && showDiscountInBasket && discountCodesEnabled;
  const shouldShowShippingMerchant = config.basket.showMerchantInformation;
  const isDeliveryOptionsSlotFilled = filledSlots.deliveryOptions;
  const isCustomDeliveryOption = config.features.ingrid.enabled || isDeliveryOptionsSlotFilled;

  useEffect(() => {
    if (!isMobile) {
      setIsExpanded(true);
      setShowDeliveryBadge(false);
    }
  }, [isMobile]);

  const showPriceSummary = !isMobile || isBasketTop || isAuthStep;

  const basketContent = (
    <>
      <slot name="slot_basket_top" />
      <div className="basket__middle">
        {filteredPackages.map((pkg, index) => (
          <div key={index}>
            {!!pkg.items.length && (
              <div className="basket__overview">
                {!isCustomDeliveryOption && (
                  <div className="delivery-estimate">
                    <div
                      className={`delivery-estimate--${isMobile ? 'mobile' : 'desktop'}`}
                      data-test-id="delivery-estimate"
                    >
                      {!pkg.isAvailable ?
                        <FormattedMessage id="basket.deliveryEstimateUnavailable" />
                      : <>
                          <FormattedMessage
                            id="basket.deliveryEstimate"
                            values={{
                              min: new Date(pkg.deliveryDate.min),
                              max: new Date(pkg.deliveryDate.max)
                            }}
                          />
                          &nbsp; &bull; &nbsp;
                          <img
                            className="carrier-badge"
                            src={getAssetUrl(`carriers/${getCarrierI18nKey(pkg.carrierGroupKey!)}.svg`)}
                            data-test-id="delivery-estimate-carrier-badge"
                          />
                        </>
                      }
                    </div>
                  </div>
                )}
                {pkg.shippingMerchantName && shouldShowShippingMerchant && (
                  <div className="shipping-merchant" data-test-id="shipping-merchant">
                    <Paragraph className="shipping-merchant__paragraph">
                      <FormattedMessage id="basket.merchant.shippedBy" />{' '}
                      <span className="shipping-merchant__paragraph--bold" data-test-id="shipping-merchant-name">
                        {pkg.shippingMerchantName}
                      </span>
                    </Paragraph>
                  </div>
                )}
              </div>
            )}
            <div className="basket__content">
              {groupItems(pkg.items, basket.total).map(item => (
                <BasketItem key={item.id} item={item} isSoldOut={!pkg.isAvailable} />
              ))}
            </div>
          </div>
        ))}
      </div>
      {showDiscountCodes && (
        <>
          <SubHeadline>
            <FormattedMessage id="payment.voucher.headline" />
          </SubHeadline>
          <DiscountCodes />
        </>
      )}
      <slot name="slot_basket_bottom" />
      {showPriceSummary && <PriceSummary total={basketTotal} shippingCost={basketShippingCost} />}
    </>
  );

  const middleBasket =
    isCollapsible ?
      <Collapse isOpen={isExpanded} onClose={() => setShowDeliveryBadge(true)}>
        {basketContent}
      </Collapse>
    : basketContent;

  return (
    <div
      className={classNames('basket-container', { 'basket-container--bottom': isPaymentStep && !isBasketTop })}
      ref={basketRef}
      data-test-id="basket-container"
    >
      <div className="basket__modal" style={isMobile ? { top: 'unset' } : desktopStickyStyle}>
        <div className={classNames('basket', { 'basket--web-component': IS_WEB_COMPONENT })} data-test-id="basket">
          <MainHeadline
            className={classNames('basket__headline', {
              ['basket__headline--breadcrumbs-offset']: isBreadcrumbsEnabled
            })}
          >
            <FormattedMessage id="basket.headline" />
            {isCollapsible && (
              <div className="basket__expand">
                <Price price={basketTotal} data-test-id="basket-total-price" />
                <img
                  className="basket__chevron-icon"
                  onClick={() => {
                    setIsExpanded(!isExpanded);
                    if (showDeliveryBadge) {
                      setShowDeliveryBadge(false);
                    }
                  }}
                  src={getAssetUrl('inputs/chevronDown.svg')}
                  style={{ transform: `rotate(${isExpanded ? '180deg' : '0'})` }}
                />
              </div>
            )}
          </MainHeadline>
          <div className="basket__inner">
            {showDeliveryBadge && <BasketSummaryBadges packages={basket.packages} carrier={selectedCarrier} />}
            {middleBasket}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Basket;
